import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest, withCsrfHeader } from "@/lib/queryClient";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { CcEmailInput } from "@/components/cc-email-input";
import { useToast } from "@/hooks/use-toast";
import { useEffectivePermissions } from "@/hooks/use-effective-permissions";
import {
  FileSignature, Send, Upload, Clock, CheckCircle2, XCircle, AlertCircle,
  Search, FileText, RefreshCw, Plus, Pencil, Trash2, MoreVertical,
  ChevronDown, ChevronRight, Loader2, Users, Archive, Eye, History, ArrowRight, Download
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { format, formatDistanceToNow } from "date-fns";
import type { UserRole } from "@shared/schema";

interface FormsPageProps {
  effectiveRole: UserRole;
  effectiveUserId: string;
}

type QueueClient = {
  client: any;
  instances: any[];
};

type QueueData = {
  waiting: QueueClient[];
  pending: QueueClient[];
  completed: QueueClient[];
};

const DOC_STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  sent: "Pending",
  completed: "Completed",
  declined: "Declined",
  voided: "Voided",
};

const DOC_STATUS_COLORS: Record<string, string> = {
  sent: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  completed: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  declined: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  voided: "bg-muted text-muted-foreground",
  draft: "bg-muted text-muted-foreground",
};

export default function FormsPage({ effectiveRole, effectiveUserId }: FormsPageProps) {
  const { toast } = useToast();
  const { permissions } = useEffectivePermissions();
  const visualBuilderAllowed = permissions["forms.feature.visualBuilder"] === true;
  const [activeTab, setActiveTab] = useState("waiting");
  const [searchQuery, setSearchQuery] = useState("");

  // Dialogs
  const [sendDialogClient, setSendDialogClient] = useState<QueueClient | null>(null);
  const [sendRecipientEmail, setSendRecipientEmail] = useState("");
  const [sendRecipientName, setSendRecipientName] = useState("");
  const [sendRecipientCc, setSendRecipientCc] = useState("");
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  const [sendMethod, setSendMethod] = useState<"upload" | "esign">("esign");

  const [completeDialogDoc, setCompleteDialogDoc] = useState<{ instance: any; clientId: string } | null>(null);
  const [uploadingComplete, setUploadingComplete] = useState(false);

  const [createTemplateOpen, setCreateTemplateOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<any | null>(null);
  const [templateForm, setTemplateForm] = useState({ name: "", templateType: "contract", deliveryMethod: "internal" });
  const [templateUploading, setTemplateUploading] = useState(false);
  const [pendingExpanded, setPendingExpanded] = useState<Set<string>>(new Set());
  const [docToDelete, setDocToDelete] = useState<any | null>(null);
  const [docToEdit, setDocToEdit] = useState<any | null>(null);
  const [docEditName, setDocEditName] = useState("");
  const [historyDoc, setHistoryDoc] = useState<any | null>(null);

  // Data
  const { data: queue, isLoading: loadingQueue, refetch: refetchQueue } = useQuery<QueueData>({
    queryKey: ["/api/forms/queue"],
  });

  const { data: templates = [], isLoading: loadingTemplates, refetch: refetchTemplates } = useQuery<any[]>({
    queryKey: ["/api/document-templates"],
  });

  // Task #243 — change log for the document open in the history dialog.
  const { data: historyChanges = [], isLoading: loadingHistory } = useQuery<any[]>({
    queryKey: ["/api/document-instances", historyDoc?.id, "changes"],
    enabled: !!historyDoc?.id,
  });

  const publishedTemplates = useMemo(() => templates.filter((t: any) => t.status === "published"), [templates]);

  // Filtered lists
  const filteredWaiting = useMemo(() => {
    const list = queue?.waiting || [];
    if (!searchQuery) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(({ client }) =>
      client.accountName?.toLowerCase().includes(q) ||
      client.contactName?.toLowerCase().includes(q) ||
      client.emailAddress?.toLowerCase().includes(q)
    );
  }, [queue?.waiting, searchQuery]);

  const filteredPending = useMemo(() => {
    const list = queue?.pending || [];
    if (!searchQuery) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(({ client }) =>
      client.accountName?.toLowerCase().includes(q) ||
      client.contactName?.toLowerCase().includes(q)
    );
  }, [queue?.pending, searchQuery]);

  const filteredCompleted = useMemo(() => {
    const list = queue?.completed || [];
    if (!searchQuery) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(({ client }) =>
      client.accountName?.toLowerCase().includes(q) ||
      client.contactName?.toLowerCase().includes(q)
    );
  }, [queue?.completed, searchQuery]);

  // Mutations
  const sendDocsMutation = useMutation({
    mutationFn: ({ clientId, templateIds, recipientEmail, recipientName, cc }: any) =>
      apiRequest("POST", `/api/clients/${clientId}/send-documents`, { templateIds, recipientEmail, recipientName, cc }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/forms/queue"] });
      queryClient.invalidateQueries({ queryKey: ["/api/document-instances"] });
      setSendDialogClient(null);
      setSelectedTemplateIds([]);
      toast({ title: "Documents sent", description: "Documents have been sent to the client." });
    },
    onError: () => toast({ title: "Failed to send", variant: "destructive" }),
  });

  const sendForSignatureMutation = useMutation({
    mutationFn: ({ clientId, templateIds, recipientEmail, recipientName, cc }: any) =>
      apiRequest("POST", `/api/clients/${clientId}/send-for-signature`, { templateIds, recipientEmail, recipientName, cc }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/forms/queue"] });
      queryClient.invalidateQueries({ queryKey: ["/api/document-instances"] });
      setSendDialogClient(null);
      setSelectedTemplateIds([]);
      toast({
        title: "Signing link sent",
        description: "The recipient will receive an email with a secure signing link.",
      });
    },
    onError: () => toast({ title: "Failed to send signing link", variant: "destructive" }),
  });

  const markCompleteMutation = useMutation({
    mutationFn: (instanceId: string) =>
      apiRequest("PATCH", `/api/document-instances/${instanceId}`, { status: "completed" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/forms/queue"] });
      queryClient.invalidateQueries({ queryKey: ["/api/document-instances"] });
      setCompleteDialogDoc(null);
      toast({ title: "Marked as completed" });
    },
    onError: () => toast({ title: "Failed to update", variant: "destructive" }),
  });

  const createTemplateMutation = useMutation({
    mutationFn: (data: any) => apiRequest("POST", "/api/document-templates", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/document-templates"] });
      setCreateTemplateOpen(false);
      setEditingTemplate(null);
      setTemplateForm({ name: "", templateType: "contract", deliveryMethod: "internal" });
      toast({ title: "Template created" });
    },
    onError: () => toast({ title: "Failed to create template", variant: "destructive" }),
  });

  const updateTemplateMutation = useMutation({
    mutationFn: ({ id, data }: any) => apiRequest("PATCH", `/api/document-templates/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/document-templates"] });
      setCreateTemplateOpen(false);
      setEditingTemplate(null);
      setTemplateForm({ name: "", templateType: "contract", deliveryMethod: "internal" });
      toast({ title: "Template updated" });
    },
    onError: () => toast({ title: "Failed to update template", variant: "destructive" }),
  });

  const publishTemplateMutation = useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/document-templates/${id}/publish`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["/api/document-templates"] }); toast({ title: "Template published" }); },
    onError: () => toast({ title: "Failed to publish", variant: "destructive" }),
  });

  const archiveTemplateMutation = useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/document-templates/${id}/archive`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["/api/document-templates"] }); toast({ title: "Template archived" }); },
    onError: () => toast({ title: "Failed to archive", variant: "destructive" }),
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/document-templates/${id}`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["/api/document-templates"] }); toast({ title: "Template deleted" }); },
    onError: () => toast({ title: "Failed to delete", variant: "destructive" }),
  });

  const deleteDocInstanceMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/document-instances/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/forms/queue"] });
      queryClient.invalidateQueries({ queryKey: ["/api/document-instances"] });
      setDocToDelete(null);
      toast({ title: "Document deleted" });
    },
    onError: () => toast({ title: "Failed to delete document", variant: "destructive" }),
  });

  const renameDocInstanceMutation = useMutation({
    mutationFn: ({ id, displayName }: { id: string; displayName: string }) =>
      apiRequest("PATCH", `/api/document-instances/${id}`, { displayName }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/forms/queue"] });
      queryClient.invalidateQueries({ queryKey: ["/api/document-instances"] });
      setDocToEdit(null);
      toast({ title: "Document updated" });
    },
    onError: () => toast({ title: "Failed to update document", variant: "destructive" }),
  });

  function openDocFile(inst: any) {
    window.open(`/api/document-instances/${inst.id}/file`, "_blank");
  }

  // Handlers
  const handleOpenSendDialog = (item: QueueClient) => {
    setSendDialogClient(item);
    setSendRecipientEmail(item.client.emailAddress || "");
    setSendRecipientName(item.client.contactName || item.client.accountName || "");
    setSelectedTemplateIds([]);
  };

  const handleSendForms = () => {
    if (!sendDialogClient || selectedTemplateIds.length === 0) return;
    if (!sendRecipientEmail) { toast({ title: "Recipient email required", variant: "destructive" }); return; }
    const payload = {
      clientId: sendDialogClient.client.id,
      templateIds: selectedTemplateIds,
      recipientEmail: sendRecipientEmail,
      recipientName: sendRecipientName,
      cc: sendRecipientCc || undefined,
    };
    if (sendMethod === "esign") {
      sendForSignatureMutation.mutate(payload);
    } else {
      sendDocsMutation.mutate(payload);
    }
  };

  const handleUploadCompleted = async (instanceId: string) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf,.doc,.docx,.png,.jpg,.jpeg";
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      setUploadingComplete(true);
      try {
        const ct = file.type || "application/octet-stream";
        const upRes = await fetch(
          `/api/documents/upload-encrypted?purpose=document_instance&instanceId=${encodeURIComponent(instanceId)}`,
          {
            method: "POST",
            credentials: "include",
            headers: await withCsrfHeader({
              "Content-Type": ct,
              "x-file-name": file.name,
              "x-file-content-type": ct,
            }),
            body: file,
          },
        );
        if (!upRes.ok) throw new Error(`upload failed (${upRes.status})`);
        const upData = await upRes.json();
        await apiRequest("POST", `/api/document-instances/${instanceId}/upload-completed`, {
          objectKey: upData.objectPath,
          fileName: file.name,
        });
        queryClient.invalidateQueries({ queryKey: ["/api/forms/queue"] });
        queryClient.invalidateQueries({ queryKey: ["/api/document-instances"] });
        setCompleteDialogDoc(null);
        toast({ title: "Document uploaded and marked complete" });
      } catch {
        toast({ title: "Upload failed", variant: "destructive" });
      } finally {
        setUploadingComplete(false);
      }
    };
    input.click();
  };

  const handleOpenTemplate = (template?: any) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateForm({ name: template.name, templateType: template.templateType, deliveryMethod: template.deliveryMethod });
    } else {
      setEditingTemplate(null);
      setTemplateForm({ name: "", templateType: "contract", deliveryMethod: "internal" });
    }
    setCreateTemplateOpen(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim()) { toast({ title: "Template name required", variant: "destructive" }); return; }
    if (editingTemplate) {
      updateTemplateMutation.mutate({ id: editingTemplate.id, data: templateForm });
    } else {
      createTemplateMutation.mutate({ ...templateForm, createdByUserId: effectiveUserId });
    }
  };

  const handleUploadTemplateFile = async (templateId: string) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf,.doc,.docx";
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      setTemplateUploading(true);
      try {
        const ct = file.type || "application/octet-stream";
        const upRes = await fetch(
          `/api/documents/upload-encrypted?purpose=document_template&templateId=${encodeURIComponent(templateId)}`,
          {
            method: "POST",
            credentials: "include",
            headers: await withCsrfHeader({
              "Content-Type": ct,
              "x-file-name": file.name,
              "x-file-content-type": ct,
            }),
            body: file,
          },
        );
        if (!upRes.ok) throw new Error(`upload failed (${upRes.status})`);
        const upData = await upRes.json();
        await apiRequest("PATCH", `/api/document-templates/${templateId}`, {
          fileObjectKey: upData.objectPath,
          fileName: file.name,
          fileMime: ct,
        });
        queryClient.invalidateQueries({ queryKey: ["/api/document-templates"] });
        toast({ title: "File uploaded" });
      } catch {
        toast({ title: "File upload failed", variant: "destructive" });
      } finally {
        setTemplateUploading(false);
      }
    };
    input.click();
  };

  const toggleExpanded = (clientId: string) => {
    setPendingExpanded(prev => {
      const next = new Set(prev);
      next.has(clientId) ? next.delete(clientId) : next.add(clientId);
      return next;
    });
  };

  const toggleTemplateId = (id: string) => {
    setSelectedTemplateIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const stats = {
    waiting: queue?.waiting?.length || 0,
    pending: queue?.pending?.length || 0,
    completed: queue?.completed?.length || 0,
    templates: publishedTemplates.length,
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 border-b bg-background">
        <div className="flex items-center justify-between p-4 pb-3">
          <div className="flex items-center gap-2">
            <FileSignature className="h-6 w-6 text-primary" />
            <h1 className="text-lg sm:text-2xl font-bold" data-testid="text-page-title">Documents</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => { refetchQueue(); refetchTemplates(); }} data-testid="button-refresh">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-4 pb-4">
          {[
            { label: "Waiting", value: stats.waiting, icon: Clock, color: "text-yellow-600 dark:text-yellow-400", bg: "bg-yellow-100 dark:bg-yellow-900/30", tab: "waiting" },
            { label: "Pending", value: stats.pending, icon: Send, color: "text-blue-600 dark:text-blue-400", bg: "bg-blue-100 dark:bg-blue-900/30", tab: "pending" },
            { label: "Completed", value: stats.completed, icon: CheckCircle2, color: "text-green-600 dark:text-green-400", bg: "bg-green-100 dark:bg-green-900/30", tab: "completed" },
            { label: "Templates", value: stats.templates, icon: FileText, color: "text-primary", bg: "bg-primary/10", tab: "templates" },
          ].map((stat) => (
            <Card key={stat.label} className="cursor-pointer hover-elevate" onClick={() => setActiveTab(stat.tab)} data-testid={`stat-${stat.label.toLowerCase()}`}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                    <p className="text-2xl font-bold">{stat.value}</p>
                  </div>
                  <div className={`p-2 rounded-lg ${stat.bg}`}>
                    <stat.icon className={`w-5 h-5 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-hidden p-4">
        <Tabs value={!visualBuilderAllowed && activeTab === "templates" ? "waiting" : activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
          <div className="flex items-center justify-between mb-3 flex-shrink-0">
            <TabsList>
              <TabsTrigger value="waiting" data-testid="tab-waiting">
                Waiting {stats.waiting > 0 && <span className="ml-1.5 text-xs bg-yellow-500 text-white rounded-full px-1.5">{stats.waiting}</span>}
              </TabsTrigger>
              <TabsTrigger value="pending" data-testid="tab-pending">
                Pending {stats.pending > 0 && <span className="ml-1.5 text-xs bg-blue-500 text-white rounded-full px-1.5">{stats.pending}</span>}
              </TabsTrigger>
              <TabsTrigger value="completed" data-testid="tab-completed">Completed</TabsTrigger>
              {visualBuilderAllowed && (
                <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
              )}
            </TabsList>
            {activeTab !== "templates" && (
              <div className="relative w-56">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search clients..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 h-8"
                  data-testid="input-search"
                />
              </div>
            )}
            {activeTab === "templates" && (
              <Button size="sm" onClick={() => handleOpenTemplate()} data-testid="button-create-template">
                <Plus className="w-4 h-4 mr-2" />
                New Template
              </Button>
            )}
          </div>

          {/* WAITING TAB */}
          <TabsContent value="waiting" className="flex-1 overflow-auto mt-0">
            {loadingQueue ? (
              <div className="flex items-center justify-center h-40"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
            ) : filteredWaiting.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-center">
                <CheckCircle2 className="w-10 h-10 text-muted-foreground mb-3 opacity-50" />
                <p className="font-medium">No clients waiting</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {searchQuery ? "No results for your search" : "All clients with form requirements have been sent their documents."}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredWaiting.map(({ client }) => (
                  <Card
                    key={client.id}
                    className="p-4 cursor-pointer hover-elevate"
                    onDoubleClick={() => handleOpenSendDialog({ client, instances: [] })}
                    data-testid={`waiting-client-${client.id}`}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-8 h-8 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center shrink-0">
                          <Clock className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium truncate" data-testid={`waiting-name-${client.id}`}>{client.accountName}</p>
                          <p className="text-sm text-muted-foreground truncate">{client.contactName && `${client.contactName} • `}{client.emailAddress}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge variant="outline" className="text-yellow-600 border-yellow-300 dark:text-yellow-400 dark:border-yellow-700 text-xs">
                          Send Documents
                        </Badge>
                        <Button size="sm" onClick={(e) => { e.stopPropagation(); handleOpenSendDialog({ client, instances: [] }); }} data-testid={`btn-send-${client.id}`}>
                          <Send className="w-3 h-3 mr-1" />
                          Send
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2 ml-11">Double-click to assign and send documents</p>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* PENDING TAB */}
          <TabsContent value="pending" className="flex-1 overflow-auto mt-0">
            {loadingQueue ? (
              <div className="flex items-center justify-center h-40"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
            ) : filteredPending.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-center">
                <Send className="w-10 h-10 text-muted-foreground mb-3 opacity-50" />
                <p className="font-medium">No pending documents</p>
                <p className="text-sm text-muted-foreground mt-1">{searchQuery ? "No results for your search" : "Documents you've sent will appear here."}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredPending.map(({ client, instances }) => {
                  const expanded = pendingExpanded.has(client.id);
                  const sentInstances = instances.filter((i: any) => i.status === "sent");
                  return (
                    <Card key={client.id} className="overflow-hidden" data-testid={`pending-client-${client.id}`}>
                      <div
                        className="flex items-center justify-between gap-4 p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                        onClick={() => toggleExpanded(client.id)}
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
                          <div className="w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
                            <Clock className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate">{client.accountName}</p>
                            <p className="text-sm text-muted-foreground">{sentInstances.length} document{sentInstances.length !== 1 ? "s" : ""} pending</p>
                          </div>
                        </div>
                        <Badge variant="outline" className="text-blue-600 border-blue-300 dark:text-blue-400 dark:border-blue-700 text-xs shrink-0">
                          Docs Pending
                        </Badge>
                      </div>

                      {expanded && (
                        <div className="border-t border-border divide-y divide-border">
                          {instances.map((inst: any) => (
                            <div key={inst.id} className="flex items-center justify-between gap-3 px-4 py-3 bg-muted/20" data-testid={`pending-doc-${inst.id}`}>
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                                <div className="min-w-0">
                                  <p className="text-sm font-medium truncate">{inst.templateName || inst.displayName || "Document"}</p>
                                  <p className="text-xs text-muted-foreground">
                                    Sent {inst.sentAt ? formatDistanceToNow(new Date(inst.sentAt), { addSuffix: true }) : "—"}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                {inst.signatureMethod === "esign" && inst.signingToken && (
                                  <Badge variant="outline" className="text-xs gap-1 text-purple-600 border-purple-300 dark:text-purple-400 dark:border-purple-700" data-testid={`badge-esign-${inst.id}`}>
                                    <FileSignature className="w-3 h-3" />
                                    E-Sign
                                  </Badge>
                                )}
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${DOC_STATUS_COLORS[inst.status] || "bg-muted text-muted-foreground"}`}>
                                  {DOC_STATUS_LABELS[inst.status] || inst.status}
                                </span>
                                {inst.status === "sent" && (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`btn-doc-actions-${inst.id}`}>
                                        <MoreVertical className="w-3.5 h-3.5" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {inst.signatureMethod === "esign" && inst.signingToken && (
                                        <DropdownMenuItem
                                          onClick={() => {
                                            const link = `${window.location.origin}/sign/${inst.signingToken}`;
                                            navigator.clipboard.writeText(link).then(
                                              () => toast({ title: "Link copied", description: link }),
                                              () => toast({ title: "Copy failed", variant: "destructive" }),
                                            );
                                          }}
                                          data-testid={`menu-copy-link-${inst.id}`}
                                        >
                                          <FileSignature className="w-4 h-4 mr-2" />
                                          Copy signing link
                                        </DropdownMenuItem>
                                      )}
                                      <DropdownMenuItem onClick={() => handleUploadCompleted(inst.id)} data-testid={`menu-upload-${inst.id}`}>
                                        <Upload className="w-4 h-4 mr-2" />
                                        Upload Completed Doc
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => markCompleteMutation.mutate(inst.id)} data-testid={`menu-complete-${inst.id}`}>
                                        <CheckCircle2 className="w-4 h-4 mr-2" />
                                        Mark as Completed
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem onClick={() => setHistoryDoc(inst)} data-testid={`menu-history-${inst.id}`}>
                                        <History className="w-4 h-4 mr-2" />
                                        Change history
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* COMPLETED TAB */}
          <TabsContent value="completed" className="flex-1 overflow-auto mt-0">
            {loadingQueue ? (
              <div className="flex items-center justify-center h-40"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
            ) : filteredCompleted.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-center">
                <CheckCircle2 className="w-10 h-10 text-muted-foreground mb-3 opacity-50" />
                <p className="font-medium">No completed clients yet</p>
                <p className="text-sm text-muted-foreground mt-1">{searchQuery ? "No results for your search" : "Completed document workflows will appear here."}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredCompleted.map(({ client, instances }) => (
                  <Card key={client.id} className="p-4" data-testid={`completed-client-${client.id}`}>
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center shrink-0">
                          <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium truncate">{client.accountName}</p>
                          <p className="text-sm text-muted-foreground">
                            {instances.length} document{instances.length !== 1 ? "s" : ""} completed
                            {instances[0]?.lastUpdatedAt && ` • ${format(new Date(instances[0].lastUpdatedAt), "MMM d, yyyy")}`}
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-green-600 border-green-300 dark:text-green-400 dark:border-green-700 text-xs shrink-0">
                        Completed
                      </Badge>
                    </div>
                    <div className="mt-3 ml-11 space-y-1.5">
                      {instances.map((inst: any) => (
                        <div key={inst.id} className="flex items-center justify-between gap-3 group" data-testid={`completed-doc-${inst.id}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                            <span className="text-sm text-foreground truncate">
                              {inst.templateName || inst.displayName || "Document"}
                            </span>
                            {inst.signatureMethod === "esign" && (
                              <Badge variant="outline" className="text-[10px] gap-1 px-1.5 py-0 h-5 text-purple-600 border-purple-300 dark:text-purple-400 dark:border-purple-700">
                                <FileSignature className="w-2.5 h-2.5" />
                                E-Signed
                              </Badge>
                            )}
                            {inst.signedSignatureDataUrl && (
                              <img
                                src={inst.signedSignatureDataUrl}
                                alt="Signature"
                                className="h-6 w-auto max-w-[80px] object-contain border border-border rounded bg-white"
                                data-testid={`img-signature-${inst.id}`}
                              />
                            )}
                            {inst.signedTypedName && !inst.signedSignatureDataUrl && (
                              <span className="text-xs italic font-serif text-foreground" style={{ fontFamily: '"Brush Script MT", "Lucida Handwriting", cursive' }} data-testid={`text-typed-signature-${inst.id}`}>
                                {inst.signedTypedName}
                              </span>
                            )}
                            {inst.signedAuditHash && (
                              <span
                                className="text-[10px] font-mono text-muted-foreground"
                                title={`Audit hash: ${inst.signedAuditHash}`}
                                data-testid={`text-audit-hash-${inst.id}`}
                              >
                                #{inst.signedAuditHash.slice(0, 8)}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              title="View document"
                              data-testid={`btn-view-doc-${inst.id}`}
                              onClick={() => openDocFile(inst)}
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </Button>
                            {inst.signedDocumentObjectKey && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                title="Download signed copy"
                                data-testid={`btn-signed-archive-${inst.id}`}
                                onClick={() => window.open(`/api/document-instances/${inst.id}/signed-archive?download=true`, "_blank")}
                              >
                                <Download className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              title="Change history"
                              data-testid={`btn-history-doc-${inst.id}`}
                              onClick={() => setHistoryDoc(inst)}
                            >
                              <History className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              title="Edit document name"
                              data-testid={`btn-edit-doc-${inst.id}`}
                              onClick={() => { setDocToEdit(inst); setDocEditName(inst.displayName || inst.templateName || ""); }}
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-destructive hover:text-destructive"
                              title="Delete document"
                              data-testid={`btn-delete-doc-${inst.id}`}
                              onClick={() => setDocToDelete(inst)}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* TEMPLATES TAB */}
          <TabsContent value="templates" className="flex-1 overflow-auto mt-0">
            {loadingTemplates ? (
              <div className="flex items-center justify-center h-40"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
            ) : templates.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-center">
                <FileText className="w-10 h-10 text-muted-foreground mb-3 opacity-50" />
                <p className="font-medium">No templates yet</p>
                <p className="text-sm text-muted-foreground mt-1">Create your first template to start sending forms to clients.</p>
                <Button className="mt-4" size="sm" onClick={() => handleOpenTemplate()} data-testid="button-create-first-template">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Template
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {templates.map((template: any) => (
                  <Card key={template.id} className="p-4" data-testid={`template-${template.id}`}>
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <FileText className="w-4 h-4 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium" data-testid={`template-name-${template.id}`}>{template.name}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              template.status === "published" ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" :
                              template.status === "archived" ? "bg-muted text-muted-foreground" :
                              "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
                            }`}>
                              {template.status}
                            </span>
                            <span className="text-xs text-muted-foreground capitalize">{template.templateType}</span>
                            {template.deliveryMethod === "docusign" && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 font-medium">DocuSign</span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {template.fileName ? `📎 ${template.fileName}` : "No file attached"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {template.status !== "archived" && template.status !== "published" && (
                          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => handleUploadTemplateFile(template.id)} disabled={templateUploading} data-testid={`btn-upload-file-${template.id}`}>
                            <Upload className="w-3 h-3 mr-1" />
                            {template.fileName ? "Replace File" : "Attach File"}
                          </Button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`btn-template-menu-${template.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleOpenTemplate(template)} data-testid={`menu-edit-${template.id}`}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            {template.status === "draft" && (
                              <DropdownMenuItem onClick={() => publishTemplateMutation.mutate(template.id)} data-testid={`menu-publish-${template.id}`}>
                                <Eye className="w-4 h-4 mr-2" />
                                Publish
                              </DropdownMenuItem>
                            )}
                            {template.status === "published" && (
                              <DropdownMenuItem onClick={() => handleUploadTemplateFile(template.id)} data-testid={`menu-attach-${template.id}`}>
                                <Upload className="w-4 h-4 mr-2" />
                                {template.fileName ? "Replace File" : "Attach File"}
                              </DropdownMenuItem>
                            )}
                            {template.status !== "archived" && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => archiveTemplateMutation.mutate(template.id)} data-testid={`menu-archive-${template.id}`}>
                                  <Archive className="w-4 h-4 mr-2" />
                                  Archive
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive" onClick={() => deleteTemplateMutation.mutate(template.id)} data-testid={`menu-delete-${template.id}`}>
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* SEND FORMS DIALOG */}
      <Dialog open={!!sendDialogClient} onOpenChange={(open) => !open && setSendDialogClient(null)}>
        <DialogContent className="max-w-lg" data-testid="dialog-send-forms">
          <DialogHeader>
            <DialogTitle>Assign & Send Documents</DialogTitle>
            <DialogDescription>
              Select the documents to send to <strong>{sendDialogClient?.client?.accountName}</strong>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Method selector — Native e-sign or email PDF for upload */}
            <div className="space-y-2">
              <p className="text-sm font-medium">Delivery method</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setSendMethod("esign")}
                  className={`text-left rounded-lg border-2 p-3 transition-colors ${
                    sendMethod === "esign"
                      ? "border-primary bg-primary/5"
                      : "border-border hover:bg-muted/50"
                  }`}
                  data-testid="button-method-esign"
                >
                  <div className="flex items-center gap-2">
                    <FileSignature className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium">E-Signature</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Recipient signs in their browser. No login required.
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setSendMethod("upload")}
                  className={`text-left rounded-lg border-2 p-3 transition-colors ${
                    sendMethod === "upload"
                      ? "border-primary bg-primary/5"
                      : "border-border hover:bg-muted/50"
                  }`}
                  data-testid="button-method-upload"
                >
                  <div className="flex items-center gap-2">
                    <Upload className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium">Email PDF</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Send the file as an attachment. You'll upload the signed copy later.
                  </p>
                </button>
              </div>
            </div>

            {/* Recipient */}
            <div className="space-y-3 p-3 bg-muted/40 rounded-lg">
              <p className="text-sm font-medium">Recipient</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Name</Label>
                  <Input
                    value={sendRecipientName}
                    onChange={(e) => setSendRecipientName(e.target.value)}
                    placeholder="Contact name"
                    className="h-8"
                    data-testid="input-recipient-name"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Email</Label>
                  <Input
                    value={sendRecipientEmail}
                    onChange={(e) => setSendRecipientEmail(e.target.value)}
                    placeholder="Email address"
                    className="h-8"
                    data-testid="input-recipient-email"
                  />
                </div>
              </div>
              <CcEmailInput value={sendRecipientCc} onChange={setSendRecipientCc} data-testid="input-send-docs-cc" />
            </div>

            {/* Template selection */}
            <div className="space-y-2">
              <p className="text-sm font-medium">Select Documents to Send</p>
              {publishedTemplates.length === 0 ? (
                <div className="p-4 border border-dashed rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">No published templates yet.</p>
                  <Button variant="link" size="sm" className="mt-1" onClick={() => { setSendDialogClient(null); setActiveTab("templates"); }}>
                    Create a template first
                  </Button>
                </div>
              ) : (
                <div className="space-y-2 max-h-52 overflow-y-auto">
                  {publishedTemplates.map((template: any) => (
                    <label
                      key={template.id}
                      className="flex items-center gap-3 p-3 rounded-lg border border-border cursor-pointer hover:bg-muted/50 transition-colors"
                      data-testid={`template-option-${template.id}`}
                    >
                      <Checkbox
                        checked={selectedTemplateIds.includes(template.id)}
                        onCheckedChange={() => toggleTemplateId(template.id)}
                        data-testid={`checkbox-template-${template.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{template.name}</p>
                        <p className="text-xs text-muted-foreground capitalize">{template.templateType} • {template.fileName || "No file"}</p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {selectedTemplateIds.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {sendMethod === "esign"
                  ? `${selectedTemplateIds.length} signing link${selectedTemplateIds.length !== 1 ? "s" : ""} will be emailed to ${sendRecipientEmail || "the recipient"}`
                  : `${selectedTemplateIds.length} document${selectedTemplateIds.length !== 1 ? "s" : ""} will be sent to ${sendRecipientEmail || "the recipient"}`}
              </p>
            )}
          </div>

          <DialogFooter className="flex items-center justify-between">
            <Button variant="outline" onClick={() => setSendDialogClient(null)}>Cancel</Button>
            <Button
              onClick={handleSendForms}
              disabled={
                selectedTemplateIds.length === 0 ||
                !sendRecipientEmail ||
                sendDocsMutation.isPending ||
                sendForSignatureMutation.isPending
              }
              data-testid="button-confirm-send"
            >
              {(sendDocsMutation.isPending || sendForSignatureMutation.isPending) && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              {sendMethod === "esign" ? (
                <FileSignature className="w-4 h-4 mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {sendMethod === "esign"
                ? `Send ${selectedTemplateIds.length > 0 ? selectedTemplateIds.length : ""} for E-Signature`
                : `Send ${selectedTemplateIds.length > 0 ? `${selectedTemplateIds.length} Document${selectedTemplateIds.length !== 1 ? "s" : ""}` : "Documents"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CREATE / EDIT TEMPLATE DIALOG */}
      <Dialog open={createTemplateOpen} onOpenChange={(open) => { if (!open) { setCreateTemplateOpen(false); setEditingTemplate(null); } }}>
        <DialogContent data-testid="dialog-template-form">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? "Edit Template" : "Create Template"}</DialogTitle>
            <DialogDescription>
              {editingTemplate ? "Update this document template." : "Create a new form or contract template. You can attach a file after saving."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-medium">Template Name</Label>
              <Input
                value={templateForm.name}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Standard Performance Contract"
                className="mt-1"
                data-testid="input-template-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Type</Label>
                <Select value={templateForm.templateType} onValueChange={(v) => setTemplateForm(prev => ({ ...prev, templateType: v }))}>
                  <SelectTrigger className="mt-1" data-testid="select-template-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contract">Contract</SelectItem>
                    <SelectItem value="form">Form</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium">Delivery Method</Label>
                <Select value={templateForm.deliveryMethod} onValueChange={(v) => setTemplateForm(prev => ({ ...prev, deliveryMethod: v }))}>
                  <SelectTrigger className="mt-1" data-testid="select-delivery-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="internal">Email / Upload</SelectItem>
                    <SelectItem value="docusign">DocuSign (optional)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {templateForm.deliveryMethod === "docusign" && (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm text-blue-700 dark:text-blue-300">
                DocuSign integration is configured in Admin → Integrations. Templates will use your connected DocuSign account.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateTemplateOpen(false); setEditingTemplate(null); }}>Cancel</Button>
            <Button
              onClick={handleSaveTemplate}
              disabled={createTemplateMutation.isPending || updateTemplateMutation.isPending}
              data-testid="button-save-template"
            >
              {(createTemplateMutation.isPending || updateTemplateMutation.isPending) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingTemplate ? "Save Changes" : "Create Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Document Confirmation */}
      <AlertDialog open={!!docToDelete} onOpenChange={(open) => { if (!open) setDocToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this document? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-doc">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => docToDelete && deleteDocInstanceMutation.mutate(docToDelete.id)}
              disabled={deleteDocInstanceMutation.isPending}
              data-testid="button-confirm-delete-doc"
            >
              {deleteDocInstanceMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Document Name Dialog */}
      <Dialog open={!!docToEdit} onOpenChange={(open) => { if (!open) setDocToEdit(null); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Edit Document</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="doc-edit-name">Document Name</Label>
              <Input
                id="doc-edit-name"
                value={docEditName}
                onChange={(e) => setDocEditName(e.target.value)}
                placeholder="Document name"
                data-testid="input-doc-edit-name"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && docToEdit && docEditName.trim()) {
                    renameDocInstanceMutation.mutate({ id: docToEdit.id, displayName: docEditName.trim() });
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocToEdit(null)} data-testid="button-cancel-edit-doc">Cancel</Button>
            <Button
              onClick={() => docToEdit && renameDocInstanceMutation.mutate({ id: docToEdit.id, displayName: docEditName.trim() })}
              disabled={!docEditName.trim() || renameDocInstanceMutation.isPending}
              data-testid="button-save-edit-doc"
            >
              {renameDocInstanceMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CHANGE HISTORY DIALOG (Task #243) */}
      <Dialog open={!!historyDoc} onOpenChange={(open) => { if (!open) setHistoryDoc(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-4 h-4" />
              Change History
            </DialogTitle>
            <DialogDescription>
              {historyDoc?.displayName || historyDoc?.templateName || "Document"} — most recent changes first
            </DialogDescription>
          </DialogHeader>
          <div className="py-1 max-h-[60vh] overflow-auto" data-testid="list-change-history">
            {loadingHistory ? (
              <div className="flex items-center justify-center h-24">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : historyChanges.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-24 text-center">
                <History className="w-8 h-8 text-muted-foreground mb-2 opacity-50" />
                <p className="text-sm text-muted-foreground">No changes recorded yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {historyChanges.map((change: any) => (
                  <div key={change.id} className="rounded-md border border-border p-3" data-testid={`change-entry-${change.id}`}>
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="text-sm font-medium text-foreground truncate" data-testid={`text-change-field-${change.id}`}>
                        {change.fieldLabel || change.fieldKey}
                      </span>
                      <span className="text-xs text-muted-foreground shrink-0" title={change.createdAt ? new Date(change.createdAt).toLocaleString() : undefined}>
                        {change.createdAt ? formatDistanceToNow(new Date(change.createdAt), { addSuffix: true }) : "—"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400 line-through break-all" data-testid={`text-change-old-${change.id}`}>
                        {change.oldValue ?? <span className="italic opacity-70">empty</span>}
                      </span>
                      <ArrowRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      <span className="px-1.5 py-0.5 rounded bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400 break-all" data-testid={`text-change-new-${change.id}`}>
                        {change.newValue ?? <span className="italic opacity-70">empty</span>}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1.5">
                      by {change.changedByName || "Unknown"}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryDoc(null)} data-testid="button-close-history">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
